import { useCallback } from "react"
import { useHomeDiscoverySession } from "@/common/queries/use-home-discovery-session"
import {
  LazyLoadImage,
  trackWindowScroll,
} from "react-lazy-load-image-component"

import type { DiscoverySessionItem, HomeSession } from "@be-miniapp/api"
import { useSelectedServiceStore } from "@be-miniapp/shared/stores/current-service"
import { removePackageNameInRoute } from "@be-miniapp/shared/utils/string"
import { Typography } from "@be-miniapp/ui/components/ui/typography"
import { useNavigate } from "@be-miniapp/ui/hooks/use-navigate"

import { useClientInfo } from "@/store/client-info"
import { Tag } from "@/components/ui/tag"

interface HomeDiscoverySessionListProps {
  data?: DiscoverySessionItem[]
  onSessionClick: (session: DiscoverySessionItem) => void
  locale: string
  isLoading?: boolean
}

const HomeDiscoverySessionList = trackWindowScroll<any>(
  ({
    data,
    onSessionClick,
    locale,
    isLoading,
  }: HomeDiscoverySessionListProps) => {
    const lists = data ?? []

    if (isLoading) {
      return Array.from({ length: 4 }).map((_, idx) => (
        <div
          key={idx}
          className="flex w-full flex-col overflow-hidden break-words rounded-lg border border-gray-200/50"
        >
          <div className="h-40 bg-gray-200/40" />
        </div>
      ))
    }

    return lists.map((session) => {
      return (
        <div
          key={session.ad_id + session.campaign_id}
          className="flex w-full flex-col overflow-hidden break-words rounded-lg border border-gray-200/50"
          onClick={() => onSessionClick(session)}
        >
          <LazyLoadImage
            src={session.image_link[locale as keyof typeof session.image_link]}
            className="h-40 bg-cover object-cover"
            fetchPriority="low"
          />

          <div className="mt-2 flex flex-col">
            <Typography.Label size="medium" className="px-2 font-normal">
              {session.title[locale as keyof typeof session.title]}
            </Typography.Label>
            <div className="mt-auto flex flex-col gap-2 p-2">
              <div className="flex">
                <Tag color="blue">
                  {session.tag[locale as keyof typeof session.title]}
                </Tag>
              </div>
            </div>
          </div>
        </div>
      )
    })
  }
)

interface HomeDiscoverySessionProps {
  sessionMetadata: HomeSession
}

const HomeDiscoverySession = ({
  sessionMetadata,
}: HomeDiscoverySessionProps) => {
  const locale = useClientInfo((state) => state.additionals.locale)
  const { data: homeDiscoverySessions, isLoading } = useHomeDiscoverySession()

  const navigate = useNavigate()
  const resetSelectedService = useSelectedServiceStore().reset
  const onSessionClick = useCallback(
    (session: DiscoverySessionItem) => {
      const webScheme = "xyz.be.customer://web?url="

      const miniappLink = "https://zalo.me/s/2517980822879468590"

      if (session.deep_link.startsWith(miniappLink)) {
        navigate(session.deep_link.slice(miniappLink.length))
        return
      }

      if (session.deep_link.startsWith(webScheme)) {
        // openWebview({
        //   url: session.deep_link.slice(webScheme.length),
        // })

        return
      }

      resetSelectedService()

      navigate(removePackageNameInRoute(session.deep_link))
    },
    [navigate, resetSelectedService]
  )

  const isEmpty = !homeDiscoverySessions?.data?.items?.length

  return (
    !isEmpty && (
      <div className="flex animate-fade-slide-up-sm flex-col gap-3 px-4 pb-5">
        <Typography.Title size="medium" className="text-gray-800">
          {sessionMetadata?.title?.[locale]}
        </Typography.Title>
        <div className="grid grid-cols-2 gap-2 pb-safe-bottom max-fold:grid-cols-1">
          <HomeDiscoverySessionList
            data={homeDiscoverySessions?.data?.items ?? []}
            loading={isLoading}
            onSessionClick={onSessionClick}
            locale={locale}
          />
        </div>
      </div>
    )
  )
}

export { HomeDiscoverySession }
